import { Bar, getDatasetAtEvent } from 'react-chartjs-2';
import { FC, useRef } from 'react';
import { ChartData, ChartOptions } from 'chart.js';
import {
  calculateGrace,
  createTooltipLabel
} from 'common/helpers/chart-helpers';

export const ChartBar: FC<{
  title: string;
  isStackedX: boolean;
  isStackedY: boolean;
  yAxisFormat: Intl.NumberFormat;
  tooltipFormat: Intl.NumberFormat;
  xAxisTitle: string;
  yAxisTitle: string;
  yHighestValue?: number;
  shouldAnimate: boolean;
  data: ChartData<'bar', (number | [number, number] | null)[], unknown>;
}> = ({
  title,
  isStackedX,
  isStackedY,
  yAxisFormat,
  tooltipFormat,
  xAxisTitle,
  yAxisTitle,
  yHighestValue,
  shouldAnimate,
  data
}: {
  title: string;
  isStackedX: boolean;
  isStackedY: boolean;
  yAxisFormat: Intl.NumberFormat;
  tooltipFormat: Intl.NumberFormat;
  xAxisTitle: string;
  yAxisTitle: string;
  yHighestValue?: number;
  shouldAnimate: boolean;
  data: ChartData<'bar', (number | [number, number] | null)[], unknown>;
}) => {
  const chartRef = useRef<any>();

  const onClick = (e: any) => {
    console.log(getDatasetAtEvent(chartRef.current, e));
  };

  const options: ChartOptions<'bar'> = {
    plugins: {
      title: {
        display: title.length > 0,
        text: title,
        align: 'start',
        font: {
          weight: '700',
          size: 14
        }
      },
      legend: {
        position: 'top',
        align: 'end',
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          boxHeight: 8
        }
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return createTooltipLabel(
              context.parsed.y,
              tooltipFormat,
              context.dataset.label
            );
          }
        }
      }
    },
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        stacked: isStackedX,
        grid: {
          display: false
        }
      },
      xTitle: {
        type: 'category',
        axis: 'x',
        labels: [],
        display: xAxisTitle.length > 0,
        border: {
          display: false
        },
        title: {
          align: 'center',
          text: xAxisTitle,
          display: true,
          font: {
            size: 12
          }
        }
      },
      y: {
        stacked: isStackedY,
        ticks: {
          callback: function (value) {
            const valueAsNumber = value as number;
            const formattedValue = yAxisFormat.format(valueAsNumber);

            return formattedValue;
          }
        },
        max: yHighestValue,
        grace: calculateGrace(yHighestValue ?? 0)
      },
      yTitle: {
        type: 'category',
        axis: 'y',
        labels: [],
        display: yAxisTitle.length > 0,
        border: {
          display: false
        },
        title: {
          align: 'center',
          text: yAxisTitle,
          display: true,
          font: {
            size: 12
          }
        }
      }
    }
  };

  (options as any).animation = shouldAnimate;

  return (
    <Bar
      ref={chartRef}
      options={options}
      data={data}
      onClick={onClick}
      updateMode="active"
    />
  );
};
